'use client';

import { useState, useCallback } from 'react';
import {
  Question,
  Clock,
  Truck,
  WarningCircle,
  Handshake,
  Package,
  NavigationArrow,
  CaretDown,
} from '@phosphor-icons/react';
import { cn } from '@/lib/utils';

const FAQ_ITEMS = [
  {
    id: 1,
    question: 'В течение какого времени доставят заказ?',
    answer:
      'Среднее время доставки составляет от 60 минут до 120 минут с момента подтверждения заказа, но оно может быть выше, зависит от адреса доставки, загруженности производства, погодных условий, ситуации на дорогах. Обычно мы доставляем быстро. Более точное время в текущий момент лучше уточнять у оператора call-центра.',
    icon: Clock,
  },
  {
    id: 2,
    question: 'Доставляете ли в пригороды?',
    answer:
      'Мы доставляем заказы только в зоны, обозначенные на карте доставки. В другие районы доставка осуществляется за счет клиента, с использованием стороннего сервиса «Яндекс доставка».',
    icon: Truck,
  },
  {
    id: 3,
    question: 'Возможно ли связаться с курьером?',
    answer:
      'Если плановое время доставки ещё не истекло, мы стараемся не беспокоить курьеров, руководствуясь целями безопасности и правилами поведения на дорогах. Дополнительная связь с курьером увеличивает время доставки и повышает риск перепутать заказы.',
    icon: WarningCircle,
  },
  {
    id: 4,
    question: 'К вам опоздали?',
    answer:
      'Если мы вдруг опоздали с приготовлением или доставкой заказа, то мы обязательно сделаем комплимент от лица компании. Просьба позвонить по номеру: 8(863) 333-50-50 и сообщить о задержке заказа. Оператор call-центра сориентирует по статусу вашего заказа.',
    icon: Handshake,
  },
  {
    id: 5,
    question: 'Вам что-то недовезли?',
    answer:
      'Если вы обнаружили, что в вашем заказе не хватает какого-то блюда, мы привезем его в ближайшее время или предложим вам компенсацию. Обратитесь на горячую линию для решения вопроса в течение 1 часа.',
    icon: Package,
  },
  {
    id: 6,
    question: 'Мне отправили курьера Яндекс, не предупредив!',
    answer:
      'Периодически мы пользуемся сервисом Яндекс Доставка для своевременной доставки заказов. Как только мы назначаем курьера Яндекс, вам приходит СМС, чтобы можно было отследить его в приложении ЯндексGO.',
    icon: NavigationArrow,
  },
];

export function DeliveryFAQ() {
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const toggle = useCallback((id: number) => {
    setExpandedId((prev) => (prev === id ? null : id));
  }, []);

  return (
    <section className="py-16 sm:py-24 bg-[var(--bg-secondary)] relative overflow-hidden">
      {/* Purple gradient accent */}
      <div className="blur-blob absolute -top-20 -left-20 w-[400px] h-[400px] rounded-full bg-[var(--primary)]/6 blur-[100px]" />
      <div className="blur-blob absolute bottom-0 right-0 w-[300px] h-[300px] rounded-full bg-[var(--violet-400)]/5 blur-[80px]" />
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10 sm:mb-14 animate-fade-up">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--primary-subtle)] text-[var(--primary)] text-sm font-semibold mb-4">
            <Question weight="fill" className="w-4 h-4" />
            FAQ
          </span>
          <h2 className="text-3xl sm:text-4xl font-heading font-black tracking-tight text-[var(--text-primary)] mb-4">
            Часто задаваемые{' '}
            <span className="text-[var(--primary)]">вопросы</span>
          </h2>
          <p className="text-[var(--text-secondary)] text-lg">
            Ответы на популярные вопросы о доставке
          </p>
        </div>

        <div className="space-y-3">
          {FAQ_ITEMS.map((item) => {
            const isOpen = expandedId === item.id;
            return (
              <div
                key={item.id}
                className={cn(
                  'rounded-2xl border transition-all duration-200',
                  'bg-[var(--surface-primary)]',
                  isOpen
                    ? 'border-[var(--primary)]/25 shadow-lg shadow-[var(--primary)]/5'
                    : 'border-[var(--border-default)] hover:border-[var(--border-hover)]',
                )}
              >
                <button
                  type="button"
                  onClick={() => toggle(item.id)}
                  className="w-full flex items-center gap-4 p-5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)]/30 rounded-2xl"
                  aria-expanded={isOpen}
                >
                  <div
                    className={cn(
                      'w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-all duration-200',
                      isOpen
                        ? 'bg-[var(--primary)] shadow-md shadow-[var(--primary)]/20'
                        : 'bg-[var(--primary-subtle)]',
                    )}
                  >
                    <item.icon
                      weight="fill"
                      className={cn(
                        'w-5 h-5 transition-colors duration-200',
                        isOpen ? 'text-white' : 'text-[var(--primary)]',
                      )}
                    />
                  </div>

                  <span
                    className={cn(
                      'flex-1 text-sm sm:text-base font-bold transition-colors duration-200',
                      isOpen ? 'text-[var(--primary)]' : 'text-[var(--text-primary)]',
                    )}
                  >
                    {item.question}
                  </span>

                  <div
                    className={cn(
                      'w-7 h-7 rounded-full flex items-center justify-center shrink-0 transition-all duration-200',
                      isOpen ? 'bg-[var(--primary)] rotate-180' : 'bg-[var(--primary-subtle)]',
                    )}
                  >
                    <CaretDown
                      weight="bold"
                      className={cn(
                        'w-3.5 h-3.5 transition-colors duration-200',
                        isOpen ? 'text-white' : 'text-[var(--primary)]',
                      )}
                    />
                  </div>
                </button>

                {/* CSS grid trick for smooth height animation without JS */}
                <div
                  className="grid transition-[grid-template-rows] duration-300 ease-out"
                  style={{ gridTemplateRows: isOpen ? '1fr' : '0fr' }}
                >
                  <div className="overflow-hidden">
                    <div className="px-5 pb-5 pt-0">
                      <div className="pl-14 border-t border-[var(--primary)]/10 pt-4">
                        <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
                          {item.answer}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
